import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TenantTypeViewStore } from "../../store";
import { TenantTypeView } from "../view/TenantTypeView";
import { TenantTypeUpdateStore } from "../../store";
import { TenantTypeUpdate } from "../update/TenantTypeUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TenantTypeModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TenantTypeModalContent: React.FC<TenantTypeModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TenantTypeUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TenantTypeViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TenantTypeView
            tenantTypeViewStore={storeInfo.store as TenantTypeViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <TenantTypeUpdate
            tenantTypeUpdateStore={storeInfo.store as TenantTypeUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
